import { ActionType } from '../../core/redux/actionType';
import { GoalAnswer } from './models';
import { Endpoints } from '../../core/api/endpoints';
import { makeRsaaTypes, makeApiAction, ApiReducer, makeApiReducer } from '../../core/api/rsaa';
import { AppState } from '../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../core/api/module';
import * as R from 'ramda';
import { toMap } from '../../language/arrays';


const KEY_ACTION = ActionType.GoalsAnswersRequest;
const ACTIONS = [
  ActionType.GoalsAnswersRequest,
  ActionType.GoalsAnswersResponse,
  ActionType.GoalsAnswersError,
];


type Payload = GoalAnswer[];


type Meta = {
  projectId: number;
}


/**
 * Dispatch this action to retrieve goals associated with the provided project and participant.
 *
 * @param projectId - the database Id for the currently selected project
 * @param participantId - the database Id for the specific participant
 */
export const goalsAnswersAction = (
  projectId: number,
  participantId: number,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method: 'POST',
    endpoint: Endpoints.GoalsAnswers,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      projectId: projectId,
      participantId: participantId,
      languageId: languageId,
    }),
    types: makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const goalsAnswers = !!payload ? toMap<GoalAnswer>(payload, goal => `${goal.questionId}`) : undefined;

  return R.mergeDeepRight(
    successState,
    {
      goalsAnswers: { [`${action.meta.projectId}`]: goalsAnswers },
    },
  );
};

export const goalsAnswersReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
